import { clamp } from 'utils/clamp';
import { featuredRankGroupCard } from './ducted-rail-rank-groups';
import { DuctedRailItem } from './ducted-rail-section-items';
import { DuctedRailSectionModel } from './ducted-rail-sections-types';
import { DuctedSectionDisplay } from './ducted-section-display-types';

const subSectionHasSystemRows = (
  subSection: DuctedRailSectionModel['subSections'][number]
): boolean => {
  const { entry } = subSection;
  if (!entry) return false;
  return (
    entry.combinations.length > 0 ||
    entry.systemTypes.length > 0 ||
    entry.totalCount > 0
  );
};

export const DUCTED_AWAITING_SKELETON_KEYS = [
  'primary',
  'secondary',
  'tertiary'
] as const;

export const isDuctedSectionAwaitingSystems = (
  section: DuctedRailSectionModel
): boolean => {
  const inPlay = section.subSections.filter(
    (subSection) => subSection.entry?.status !== 'skipped'
  );
  if (inPlay.length === 0) return false;
  if (inPlay.some(subSectionHasSystemRows)) return false;
  return inPlay.every(
    (subSection) =>
      subSection.entry == null || subSection.entry.status === 'loading'
  );
};

// A section whose settled results are being replaced: page one of a result
// set is refetching (a Sort by change, a filter or chip change), or a
// Recommended row that already has a card is refetching. The rail shows
// skeletons in place of the stale cards until every refresh settles, so the
// new order lands in one step instead of the badge and the tail moving at
// different moments. A show-more fetch is not a refresh (append leaves
// `refreshing` false), so the loaded cards stay put for it.
export const isDuctedSectionRefreshing = (
  section: DuctedRailSectionModel
): boolean =>
  section.subSections.some(
    (subSection) =>
      subSection.entry?.refreshing === true ||
      subSection.representativeEntries.some(
        ({ representative }) =>
          representative?.status === 'loading' &&
          representative.combination != null
      )
  );

// How many skeletons stand in for the cards being replaced: one per card on
// screen, at least the initial-load count, never more than a page.
export const refreshingSkeletonCount = (
  visibleCardCount: number,
  pageSize: number
): number =>
  clamp(
    visibleCardCount,
    DUCTED_AWAITING_SKELETON_KEYS.length,
    Math.max(pageSize, DUCTED_AWAITING_SKELETON_KEYS.length)
  );

// The one state a section's grid is in. The initial-load skeletons take
// precedence while nothing has settled and no Recommended card is up yet;
// a refresh shows one skeleton per card it is replacing; otherwise the
// cards.
export const ductedSectionDisplay = (
  section: DuctedRailSectionModel,
  items: DuctedRailItem[],
  pageSize: number
): DuctedSectionDisplay => {
  const hasFeaturedCard = section.rankGroups.some(
    (group) => featuredRankGroupCard(group) != null
  );
  if (!hasFeaturedCard && isDuctedSectionAwaitingSystems(section)) {
    return { kind: 'awaiting', skeletonKeys: DUCTED_AWAITING_SKELETON_KEYS };
  }
  if (isDuctedSectionRefreshing(section)) {
    return {
      kind: 'refreshing',
      skeletonCount: refreshingSkeletonCount(items.length, pageSize)
    };
  }
  return { kind: 'items', items };
};
